import React, { useState, useEffect, forwardRef, useRef, useImperativeHandle, useMemo } from 'react';
import { cloneDeep, orderBy, isEmpty, round, uniqBy } from 'lodash';
import { useTranslation } from 'react-i18next';

import { useSelector, useDispatch } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import * as action from '../../../store/DevelopmentOrder.action';
import SpecificationsView from '../Specifications/SpecificationsView';
import { checkArray } from '../../../../../Components/helper';
import DragDropSort from '../../../../../Components/DragDropSort/DragDropSort';

const SupplierSpecification = forwardRef((props, ref) => {
  const {
    SampleAddEditAccess,
    SampleDeleteAccess,
    isProductArchived,
    selectedGradGroupProp,
    setSelectedGradGroupProp,
    showSpecPomRearrange,
    onCloseSpecPomRearrange,
  } = props;
  const { t: tPS } = useTranslation('product-sample');
  const valueRef = useRef();
  const originalSpecificationDataRef = useRef([]);
  const hasAutoSelectedGradGroupRef = useRef(false);
  const dispatch = useDispatch();
  const devOrderReducer = useSelector((reducerstate) => reducerstate);
  const queryParams = useLocation();
  const { search } = queryParams;
  const smapleId = new URLSearchParams(search).get('sample');
  const { Measuremets, UserDetails } = devOrderReducer;
  const { sampleDetails, sampleSpecificationReducer } = Measuremets;
  const { isSupplierLoggedIn } = UserDetails;
  const { devOrderId } = useParams();
  const [state, setState] = useState({
    sampleSpecificationList: [],
    deleteId: null,
    pageIndex: 1,
    pageSize: 20,
    hasMore: true,
    loading: true,
    totalRecords: null,
    sortOrder: '',
    sortPropertyName: '',
    propertName: '',
    columnSortValue: 'asc',
    conversationType: '',
    fraction: '',
  });
  const [valueSelect, setValueSelect] = useState({});
  const [selectedGradGroup, setSelectedGradGroup] = useState({});
  const [gradingGroupOptions, setGradingGroupOptions] = useState([]);

  const [selectedSize, setSelectedSize] = useState({});
  const [sizeRange, setSizeRange] = useState([]);

  const pomItemsForRearrange = useMemo(() => {
    const seen = new Set();
    return (state.sampleSpecificationList || []).filter((item) => {
      if (seen.has(item.code)) return false;
      seen.add(item.code);
      return true;
    });
  }, [state.sampleSpecificationList]);

  const handleSaveSpecPomSequence = async (_e, document) => {
    const res = await dispatch(
      action.reorderSampleSpecPomSequence(
        {
          sampleId: smapleId,
          devOrderId,
          gradingGroupId: selectedGradGroup?.value,
          poms: document.poms,
        },
        tPS,
      ),
    );
    if (res) {
      onCloseSpecPomRearrange();
      if (selectedGradGroup?.value) {
        dispatch(
          action.getSampleSpecificationByGradingGroup({
            sampleId: smapleId,
            gradingGroupId: selectedGradGroup.value,
          }),
        );
      }
    }
  };

  const descendingOptions = () => {
    return state?.columnSortValue === 'desc' ? <i className="fa fa fa-sort-desc ml-1" /> : <i className="fa fa fa-sort-up ml-1" />;
  };

  const truthyValue = () => {
    return state?.columnSortValue === '' ? <i className="fa fa fa-sort ml-1" /> : descendingOptions();
  };

  const headerSortingIcon = (lable, propertyName) => {
    return (
      <span className="cursor-pointer">
        {lable}
        {state?.propertName === propertyName ? truthyValue() : <i className="fa fa fa-sort ml-1" />}
      </span>
    );
  };
  useImperativeHandle(ref, () => ({
    getSampleSupplierSpecification() {
      return {
        sampleSpecificationList: state.sampleSpecificationList,
        conversationType: state.conversationType,
        fraction: state.fraction,
        sizeName: selectedSize,
        selectedGradingGroup: selectedGradGroup,
      };
    },
    hasPendingChanges() {
      const currentData = state.sampleSpecificationList;
      const originalData = originalSpecificationDataRef.current;

      if (!currentData || !originalData) return false;
      if (currentData.length !== originalData.length) return false;

      return currentData.some((item, index) => {
        const originalItem = originalData[index];
        if (!originalItem) return false;

        return item?.supplierMeasurement !== originalItem?.supplierMeasurement || item?.supplierComments !== originalItem?.supplierComments;
      });
    },
  }));

  useEffect(() => {
    const Init = async () => {
      const params = {
        devOrderId,
      };
      setState((prevState) => ({
        ...prevState,
        loading: true,
      }));
      await dispatch(action.getSampleSpecificationData(smapleId, params));
      setState((prevState) => ({
        ...prevState,
        loading: false,
      }));
    };
    Init();
  }, [dispatch]);

  useEffect(() => {
    if (checkArray(sampleSpecificationReducer)) {
      const specificationDeepData = cloneDeep(sampleSpecificationReducer);
      setDataForConversionDiscrepancy(specificationDeepData);
    } else {
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: [],
      }));
    }
  }, [sampleSpecificationReducer]);

  function limitToFourDec(input) {
    const no = !isNaN(input) ? parseFloat(input) : input;
    return no && !isNaN(no) ? parseFloat(no.toFixed(4)) : no;
  }

  const getDiscrepancyColor = (actMeasurement, reqMeasurement, tol) => {
    const reqMaxMeasurement = reqMeasurement + tol;
    const reqMinMeasurement = reqMeasurement - tol;
    return actMeasurement >= reqMinMeasurement && actMeasurement <= reqMaxMeasurement;
  };

  const setDataForConversionDiscrepancy = (specificationDeepDataArg) => {
    let modifiedData = null;

    modifiedData = specificationDeepDataArg;

    // CALCULATE DISCREPANCY & SET DATA
    modifiedData &&
      modifiedData.length > 0 &&
      modifiedData.forEach((item, i) => {
        modifiedData[i].actualMeasurement = item.actualMeasurement ? limitToFourDec(item.actualMeasurement) : 0;
        modifiedData[i].requestedMeasurement = item.requestedMeasurement ? limitToFourDec(item.requestedMeasurement) : 0;
        modifiedData[i].supplierMeasurement = item.supplierMeasurement ? limitToFourDec(item.supplierMeasurement) : 0;
        modifiedData[i].tolMinus = item.tolMinus ? limitToFourDec(item.tolMinus) : 0;
        modifiedData[i].tolPlus = item.tolPlus ? limitToFourDec(item.tolPlus) : 0;
        modifiedData[i].revisedMeasurement = item.revisedMeasurement ? limitToFourDec(item.revisedMeasurement) : 0;

        const discrepancy = modifiedData[i] ? modifiedData[i].actualMeasurement - modifiedData[i].requestedMeasurement : NaN;

        let formattedDiscrepancy;

        if (isNaN(discrepancy)) {
          formattedDiscrepancy = 0;
        } else if (discrepancy > 0) {
          formattedDiscrepancy = `+${round(discrepancy, 4)}`;
        } else {
          formattedDiscrepancy = round(discrepancy, 4);
        }

        modifiedData[i].discrepancy = formattedDiscrepancy;
        const discrColor = getDiscrepancyColor(modifiedData[i].actualMeasurement, modifiedData[i].requestedMeasurement, modifiedData[i].tolMinus);
        modifiedData[i].discrepancyColor = discrColor ? 'green' : 'red';
      });

    // SET STATE AS PER ABOVE MODIFICATION & SAVE SIZE RANGE STATE FOR DROPDOWN
    if (!isEmpty(modifiedData)) {
      setState((prevState) => ({
        ...prevState,
        sampleSpecificationList: modifiedData,
      }));
      originalSpecificationDataRef.current = cloneDeep(modifiedData);
    }
  };

  const onSortColumns = (key, columnSortValue) => {
    let sampleSpecification = state.sampleSpecificationList;

    let columnSortVal = columnSortValue;
    if (state.propertName !== key) {
      columnSortVal = 'asc';
    } else if (columnSortVal === 'asc') {
      columnSortVal = 'desc';
    } else if (columnSortVal === 'desc') {
      columnSortVal = 'asc';
    }
    sampleSpecification = orderBy(
      sampleSpecification,
      (o) => {
        if (!isNaN(Number(o[key]))) {
          return +o[key];
        }
        return o[key];
      },
      [columnSortVal],
    );
    setState((prevState) => ({
      ...prevState,
      propertName: key,
      columnSortValue: columnSortVal,
      sampleSpecificationList: sampleSpecification,
    }));
  };
  const setDiscrepancy = async (index) => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);

    cloneStat[index].discrepancy = limitToFourDec(cloneStat[index].supplierMeasurement - cloneStat[index].requestedMeasurement);

    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const callDiffFunction = async (pIndex, frac, field) => {
    frac[field] = true;

    if (frac.mainChange && frac.numChange && frac.dnumChange) {
      setDiscrepancy(pIndex);
      frac.mainChange = false;
      frac.numChange = false;
      frac.dnumChange = false;
    }
  };

  const handleQualitySwitch = async (status) => {
    if (status === 'off') {
      setState((prevState) => ({
        ...prevState,
        fraction: 'on',
      }));
    } else {
      setState((prevState) => ({
        ...prevState,
        fraction: 'off',
      }));
    }
  };

  const onChangeSize = async (currentSize) => {
    if (selectedGradGroup?.value) {
      setValueSelect(currentSize);
    }
  };

  const handleAllMeasurement = async (index, ele, keyName) => {
    const { value } = ele.target;
    const regex = /^(\d*\.?\d{0,3})$/;
    if (!regex.test(value)) return;

    const cloneStat = cloneDeep(state.sampleSpecificationList);
    cloneStat[index][keyName] = value;

    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const handleFracMeasurement = async (index, ele, keyName, keyName2, mainKey) => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);
    cloneStat[index][keyName][keyName2] = ele.target.value;
    if (keyName2 === 'mainVal') {
      cloneStat[index][mainKey] = ele.target.value;
    }

    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const handleComment = (indices, value) => {
    const cloneStat = cloneDeep(state.sampleSpecificationList);
    indices.forEach((idx) => {
      cloneStat[idx].supplierComments = value;
    });
    setState((prevState) => ({
      ...prevState,
      sampleSpecificationList: cloneStat,
    }));
  };

  const setOptions = () => {
    if (selectedGradGroup?.value) {
      let values = [];

      if (sampleDetails?.gradingGroupData && sampleDetails?.gradingGroupData?.length > 0) {
        sampleDetails.gradingGroupData.forEach((ele) => {
          const length = ele?.len?.name;
          const size = ele?.size;
          const gradingGroup = ele?.gradingGroup?._id;
          const quan = ele?.quantity ? `(${ele?.quantity})` : ' ';
          if (size) {
            const value = length
              ? { value: ele._id, label: `${size}, ${length} ${quan}`, size, gradingGroup }
              : { value: ele._id, label: `${size} ${quan}`, size, gradingGroup };
            values.push(value);
          }
        });
        values = values.filter((ele) => ele.gradingGroup === selectedGradGroup?.value);
      }
      setSizeRange(values);
    }
  };

  useEffect(() => {
    if (selectedGradGroup && checkArray(sizeRange)) {
      const lastElementFromSizerange = sizeRange[sizeRange.length - 1];
      setValueSelect(lastElementFromSizerange);
    }
  }, [selectedGradGroup, sizeRange]);

  useEffect(() => {
    setOptions();
  }, [selectedGradGroup, sampleDetails?.gradingGroupData]);

  const onChangeGradGroup = async (gradGroup) => {
    setValueSelect({});
    setSelectedGradGroup(gradGroup);
    setState((prev) => ({ ...prev, loading: true, conversationType: gradGroup?.conversationType, fraction: gradGroup?.fraction }));
    if (setSelectedGradGroupProp) setSelectedGradGroupProp(gradGroup);
    try {
      await dispatch(
        action.getSampleSpecificationByGradingGroup({
          sampleId: smapleId,
          gradingGroupId: gradGroup.value,
        }),
      );
    } finally {
      setState((prev) => ({ ...prev, loading: false }));
    }
  };

  // Sync grading group selection from the user (Specifications) tab and fetch data
  useEffect(() => {
    if (selectedGradGroupProp?.value && selectedGradGroupProp.value !== selectedGradGroup?.value) {
      setSelectedGradGroup(selectedGradGroupProp);
      setState((prev) => ({ ...prev, loading: true, conversationType: selectedGradGroupProp?.conversationType, fraction: selectedGradGroupProp?.fraction }));
      dispatch(
        action.getSampleSpecificationByGradingGroup({
          sampleId: smapleId,
          gradingGroupId: selectedGradGroupProp.value,
        }),
      ).finally(() => setState((prev) => ({ ...prev, loading: false })));
    }
  }, [selectedGradGroupProp?.value]);

  useEffect(() => {
    valueRef.current = {
      sampleSpecificationList: state.sampleSpecificationList,
      conversationType: state.conversationType,
      fraction: state.fraction,
      sizeName: selectedSize,
    };
  }, [state.sampleSpecificationList, selectedSize, state.conversationType, state.fraction]);

  useEffect(() => {
    return () => {
      dispatch(action.unMountSampleSpecSize());
    };
  }, []);

  useEffect(() => {
    if (sampleDetails?.gradingGroupData?.length > 0) {
      let gradingOptions = sampleDetails.gradingGroupData
        .filter((ele) => ele?.gradingGroup?._id && ele?.size)
        .map((ele) => ({
          value: ele?.gradingGroup?._id,
          label: ele?.gradingGroup?.name,
          sizeRanges: ele?.sizeRanges,
          conversationType: ele?.gradingGroup?.conversationType,
          fraction: ele?.gradingGroup?.fraction,
        }));
      gradingOptions = uniqBy(gradingOptions, 'value');
      setGradingGroupOptions(gradingOptions);
    }
  }, [sampleDetails?.gradingGroupData]);

  useEffect(() => {
    if (hasAutoSelectedGradGroupRef.current) return;
    if (selectedGradGroup?.value) return;
    if (selectedGradGroupProp?.value) return;
    if (!gradingGroupOptions?.length) return;
    hasAutoSelectedGradGroupRef.current = true;
    const firstOption = gradingGroupOptions[0];
    if (setSelectedGradGroupProp) {
      setSelectedGradGroupProp(firstOption);
    } else {
      onChangeGradGroup(firstOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gradingGroupOptions, selectedGradGroup?.value, selectedGradGroupProp?.value]);
  return (
    <>
      <SpecificationsView
        sizeRange={sizeRange}
        onChangeSize={onChangeSize}
        selectedSize={selectedSize}
        valueSelect={valueSelect}
        state={state}
        handleQualitySwitch={handleQualitySwitch}
        onSortColumns={onSortColumns}
        headerSortingIcon={headerSortingIcon}
        handleAllMeasurement={handleAllMeasurement}
        callDiffFunction={callDiffFunction}
        handleFracMeasurement={handleFracMeasurement}
        setDiscrepancy={setDiscrepancy}
        handleComment={handleComment}
        isSupplierSpecification
        isSupplierLoggedIn={isSupplierLoggedIn}
        SampleDeleteAccess={SampleDeleteAccess}
        SampleAddEditAccess={SampleAddEditAccess}
        onChangeGradGroup={onChangeGradGroup}
        selectedGradGroup={selectedGradGroup}
        gradingGroups={gradingGroupOptions}
        isProductArchived={isProductArchived}
      />
      {showSpecPomRearrange && (
        <DragDropSort
          isShow={showSpecPomRearrange}
          isToggle={onCloseSpecPomRearrange}
          itemList={pomItemsForRearrange}
          saveSequence={handleSaveSpecPomSequence}
          devOrderId={devOrderId}
          sampleId={smapleId}
          fromComp="sampleSpecPom"
        />
      )}
    </>
  );
});
SupplierSpecification.displayName = 'SupplierSpecification';
export default SupplierSpecification;
